import { useCallback, useState } from "react";
import type { ReactNode } from "react";
import { Button, Modal } from "@fortex/map";
import { useApp } from "../../app/context";

export type DetailMode = "side" | "modal";

export const detailModeKey = "fortex-web:detail-mode";

export function readDetailMode(storage: Storage | null = typeof window === "undefined" ? null : window.localStorage): DetailMode {
  try {
    return storage?.getItem(detailModeKey) === "modal" ? "modal" : "side";
  } catch {
    return "side";
  }
}

export function useDetailMode(): [DetailMode, (mode: DetailMode) => void] {
  const [mode, setModeState] = useState<DetailMode>(() => readDetailMode());
  const setMode = useCallback((next: DetailMode) => {
    setModeState(next);

    try {
      window.localStorage.setItem(detailModeKey, next);
    } catch {
      return;
    }
  }, []);

  return [mode, setMode];
}

export type DetailPaneProps = {
  mode: DetailMode;
  title: string;
  subtitle?: string | null;
  onClose: () => void;
  onToggleMode: () => void;
  footer: ReactNode;
  extraFooter?: ReactNode;
  children: ReactNode;
};

export function DetailPane({ mode, title, subtitle = null, onClose, onToggleMode, footer, extraFooter, children }: DetailPaneProps) {
  const { labels } = useApp();
  const toggle = <Button kind="icon" size="sm" icon={mode === "side" ? "fullscreen" : "dock"} label={mode === "side" ? labels.openInWindow : labels.dockDetail} onClick={onToggleMode} data-fxmap="detail-mode" />;

  if (mode === "modal") {
    return (
      <Modal
        open
        title={title}
        subtitle={subtitle ?? undefined}
        onClose={onClose}
        closeLabel={labels.closeDetail}
        width="min(840px, calc(100% - 32px))"
        headerTools={toggle}
        footer={
          <>
            {extraFooter}
            {footer}
          </>
        }
        dataFxmap="detail-modal"
        className="fxapp-detail-modal"
      >
        {children}
      </Modal>
    );
  }

  return (
    <aside className="fxapp-detail fxmap-card" aria-label={title} data-fxmap="detail-pane">
      <header className="fxapp-detail-header">
        <div className="fxapp-detail-titles">
          <h2 className="fxapp-detail-title fxmap-display fxmap-truncate">{title}</h2>
          {subtitle ? <span className="fxapp-detail-subtitle fxmap-muted fxmap-mono fxmap-truncate">{subtitle}</span> : null}
        </div>
        {toggle}
        <Button kind="icon" size="sm" icon="close" label={labels.closeDetail} onClick={onClose} data-fxmap-action="close" />
      </header>
      <div className="fxapp-detail-body">{children}</div>
      {extraFooter ? <div className="fxapp-detail-extra">{extraFooter}</div> : null}
      <footer className="fxapp-detail-footer">{footer}</footer>
    </aside>
  );
}
